import React, { useState } from 'react';
import styled from 'styled-components';

import { Option } from '../../../core';
import Button from '../../ui/Button';
import Form from '../../ui/Form';
import SelectInput from '../../ui/SelectInput';
import { FrontdeskPractitionerFormInputs } from '../types';
import FrontdeskPractitionerFormContext from './FrontdeskPractitionerFormContext';
import FrontdeskPractitionerInputs from './FrontdeskPractitionerInputs';
import { renderDefaultFrontdeskPractitionerInputValues } from './helper';

export interface FrontdeskPractitionerFormProps {
  values?: FrontdeskPractitionerFormInputs;
  editing: boolean;
  onSelectPractitioner?: (data?: Option | Array<Option>) => void;
  onSubmit?: (data: FrontdeskPractitionerFormInputs) => void;
  onCancel?: () => void;
  shouldReset?: boolean;
  loadPractitioners?: any;
  practitioners?: Option[];
  selectedPractitioner?:
    | FrontdeskPractitionerFormInputs
    | Array<FrontdeskPractitionerFormInputs>
    | null;
  onRemove?: () => void;
  mode: 'primary' | 'additional';
  hpdUrl?: string;
  isSubmitOnEnter?: boolean;
  isMulti?: boolean;
  onClickAdd?: (selectedOption?: Option) => void;
  showDifferentLocationWarning?: boolean;
}

const RemoveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const RemoveButton = styled(Button)`
  height: 36px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AddButtonWrapper = styled(Button)`
  align-self: flex-end;
  min-width: 70px;
  width: 70px;
`;

const RightAlignedButtonContainer = styled.div`
  align-self: flex-end;
  justify-self: end;
`;

const RightAlignedButton = styled(Button)`
  &:first-child {
    margin-right: 20px;
  }
`;

// this is necessary for gatsby to build using the window object
const isBrowser = typeof window !== 'undefined';

const FrontdeskPractitionerForm = ({
  values,
  onCancel,
  onSubmit,
  editing,
  loadPractitioners,
  onSelectPractitioner,
  shouldReset,
  selectedPractitioner,
  onRemove,
  mode,
  hpdUrl,
  isSubmitOnEnter,
  isMulti,
  onClickAdd,
  showDifferentLocationWarning,
}: FrontdeskPractitionerFormProps) => {
  const data = renderDefaultFrontdeskPractitionerInputValues(values);
  const [selectedOption, setSelectedOption] = useState<Option | null>();

  const handleCancel = () => {
    onSelectPractitioner && onSelectPractitioner();
    onCancel && onCancel();
  };

  const handleSubmit = (data: FrontdeskPractitionerFormInputs) => {
    onSelectPractitioner && onSelectPractitioner();
    onSubmit && onSubmit(data);
  };

  const handleClickAdd = () => {
    onSelectPractitioner && onSelectPractitioner();
    onClickAdd && selectedOption && onClickAdd(selectedOption);
    setSelectedOption(null);
  };

  return (
    <FrontdeskPractitionerFormContext.Provider
      value={{
        selectedPractitioner,
        editing,
        values: data,
        mode,
        showDifferentLocationWarning,
      }}
    >
      {editing ? (
        <React.Fragment>
          <Form.Row column={3}>
            <SelectInput
              placeholder="Search Practitioner"
              label="Search Practitioner"
              isAsync
              isClearable
              loadOptions={loadPractitioners}
              onChange={(newSelectedOption: Option) => {
                setSelectedOption(newSelectedOption);
                onSelectPractitioner && onSelectPractitioner(newSelectedOption);
              }}
              isMulti={isMulti}
              value={selectedOption}
              showIndicatorAnimation={false}
            />

            <AddButtonWrapper size="medium" variant="primary" onClick={handleClickAdd}>
              Add
            </AddButtonWrapper>
            <RightAlignedButtonContainer>
              {onRemove && mode === 'primary' && (
                <RightAlignedButton size="medium" variant="secondary" onClick={() => onRemove()}>
                  Remove Practitioner
                </RightAlignedButton>
              )}
              <RightAlignedButton
                size="medium"
                variant="secondary"
                onClick={() => isBrowser && window.open(hpdUrl, '_blank')}
              >
                Create New
              </RightAlignedButton>
            </RightAlignedButtonContainer>
          </Form.Row>
        </React.Fragment>
      ) : (
        <Form
          values={data}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          shouldReset={shouldReset}
          edit={editing}
          isSubmitOnEnter={isSubmitOnEnter}
        >
          {onRemove && mode === 'additional' && (
            <TitleContainer>
              <Form.RowLabel>Additional Health Practitioner</Form.RowLabel>

              <RemoveButtonContainer>
                <RemoveButton size="small" variant="secondary" onClick={onRemove}>
                  Remove
                </RemoveButton>
              </RemoveButtonContainer>
            </TitleContainer>
          )}

          <FrontdeskPractitionerInputs />
        </Form>
      )}
    </FrontdeskPractitionerFormContext.Provider>
  );
};

export default FrontdeskPractitionerForm;
